// @spec RSK-STR-002
// @spec RSK-STR-004
// @spec RSK-STR-005
// @spec RSK-STR-010
// @spec RSK-STR-021
// @spec RSK-STR-022
// @spec RSK-STR-015
// @spec RSK-STR-016
// @spec RSK-STR-031
// @spec RSK-STR-018
// @spec RSK-STR-023
// Risk detail page with edit, approval actions, treatment selection, acceptance, linked assets, and review schedule functionality
'use client';

import { useAuth, useUser } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import * as React from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useRiskAcceptance } from '@/features/risks/api/acceptance';
import { EditRiskDialog } from '@/features/risks/components/EditRiskDialog';
import { ReviewScheduleDrawer } from '@/features/risks/components/ReviewScheduleDrawer';
import { RiskAcceptanceApproveDialog } from '@/features/risks/components/RiskAcceptanceApproveDialog';
import { RiskAcceptanceDialog } from '@/features/risks/components/RiskAcceptanceDialog';
import { RiskAcceptancePanel } from '@/features/risks/components/RiskAcceptancePanel';
import { RiskAssessmentPanel } from '@/features/risks/components/RiskAssessmentPanel';
import { RiskDetailCustomFields } from '@/features/risks/components/RiskDetailCustomFields';
import { RiskDetailHeader } from '@/features/risks/components/RiskDetailHeader';
import { RiskDetailMetadata } from '@/features/risks/components/RiskDetailMetadata';
import { RiskLinkedAssetsSection } from '@/features/risks/components/RiskLinkedAssetsSection';
import { RiskLinkedControlsSection } from '@/features/risks/components/RiskLinkedControlsSection';
import { RiskLinkedVulnerabilitiesSection } from '@/features/risks/components/RiskLinkedVulnerabilitiesSection';
import { TreatmentPlanSection } from '@/features/risks/components/TreatmentPlanSection';
import type { Risk } from '@/features/risks/types';
import { useRiskApprovalStatus } from '@/features/rsk/api/use-risk-approval';
import { NIS2MeasureLinkSection } from '@/features/nis2/components/NIS2MeasureLinkSection';
import { ApprovalActionsPanel, AIRiskAssistant, TreatmentStrategyDialog } from '@/features/rsk/components';
import type { InherentRiskLevel, TreatmentStrategy } from '@/features/rsk/types';
import { useOrganizations } from '@/hooks/use-organizations';
import { createApiClient } from '@/lib/api-client';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/risks/$riskId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.riskDetails'),
  }),
  component: RiskDetailPage,
});

type RiskDetailResponse = Risk & { treatmentStrategy?: TreatmentStrategy | null };

interface UserResponse {
  id: string;
  clerkId: string;
  email: string;
}

/**
 * Risk detail page with approval actions, treatment selection, and linked assets functionality.
 */
function RiskDetailPage() {
  const { t } = useTranslation('risks');
  const navigate = useNavigate();

  const params = useParams({ from: '/_authenticated/risks/$riskId' });

  const riskId: string = params.riskId;
  const { getToken } = useAuth();
  const { user: clerkUser } = useUser();
  const { organizations } = useOrganizations();
  const queryClient = useQueryClient();
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [isTreatmentDialogOpen, setIsTreatmentDialogOpen] = React.useState(false);
  const [isAcceptanceDialogOpen, setIsAcceptanceDialogOpen] = React.useState(false);
  const [isApproveDialogOpen, setIsApproveDialogOpen] = React.useState(false);
  const [isReviewScheduleOpen, setIsReviewScheduleOpen] = React.useState(false);
  const activeOrg = organizations.find((org) => org.isActive);

  // Fetch risk data
  const {
    data: risk,
    isLoading,
    isError,
  } = useQuery<RiskDetailResponse>({
    queryKey: ['risk', riskId],
    queryFn: async () => {
      const apiClient = createApiClient({ getAuthToken: getToken });
      return apiClient.get<RiskDetailResponse>(`/risks/${riskId}`);
    },
    enabled: !!riskId,
  });

  // Fetch current user's internal ID
  const { data: currentUser } = useQuery<UserResponse>({
    queryKey: ['currentUser', clerkUser?.id],
    queryFn: async () => {
      const apiClient = createApiClient({ getAuthToken: getToken });
      return apiClient.get<UserResponse>('/users/me');
    },
    enabled: !!clerkUser?.id,
  });

  // Fetch approval status when risk is pending approval
  const { data: approvalStatus, refetch: refetchApprovalStatus } = useRiskApprovalStatus({
    riskId,
    enabled: !!riskId && risk?.status === 'pending_approval',
  });

  // Fetch acceptance status when risk treatment is 'accept'
  const { data: acceptanceData, refetch: refetchAcceptance } = useRiskAcceptance(
    riskId,
    !!riskId && risk?.treatmentStrategy === 'accept'
  );

  const handleEditSuccess = () => {
    void queryClient.invalidateQueries({ queryKey: ['risk', riskId] });
    void queryClient.invalidateQueries({ queryKey: ['risks'] });
  };

  const handleApprovalDecision = () => {
    // Refresh risk data after approval decision
    void queryClient.invalidateQueries({ queryKey: ['risk', riskId] });
    void queryClient.invalidateQueries({ queryKey: ['risks'] });
    void refetchApprovalStatus();
  };

  const handleTreatmentSuccess = () => {
    // Refresh risk data
    void queryClient.invalidateQueries({ queryKey: ['risk', riskId] });
    void queryClient.invalidateQueries({ queryKey: ['risks'] });
  };

  const handleAcceptanceSuccess = () => {
    // Refresh acceptance data
    void refetchAcceptance();
    void queryClient.invalidateQueries({ queryKey: ['risk', riskId] });
  };

  const handleAcceptanceApprovalSuccess = () => {
    // Refresh acceptance data after approval decision
    void refetchAcceptance();
    void queryClient.invalidateQueries({ queryKey: ['risk', riskId] });
    setIsApproveDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !risk) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <Button variant="ghost" onClick={() => void navigate({ to: '/risks' })}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('detail.backToRegister')}
          </Button>
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-8 text-center">
            <h2 className="text-lg font-semibold text-destructive">{t('detail.notFound')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('detail.notFoundDesc')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const canEdit = risk.status !== 'closed';
  const isPendingApproval = risk.status === 'pending_approval';
  const canSelectTreatment = risk.status === 'open';

  // Determine if current user can approve (not their own submission)
  const canApprove =
    isPendingApproval &&
    approvalStatus?.approvalRequest &&
    currentUser &&
    approvalStatus.approvalRequest.submittedBy.id !== currentUser.id;

  // Determine if current user is the acceptance owner (for four-eyes principle)
  const isAcceptanceOwner = acceptanceData?.currentAcceptance?.ownedBy.id === currentUser?.id;

  // Determine if current user can approve the acceptance (not their own)
  const canApproveAcceptance =
    acceptanceData?.currentAcceptance?.status === 'pending_approval' &&
    currentUser &&
    !isAcceptanceOwner;

  // Show acceptance panel when treatment strategy is 'accept'
  const showAcceptancePanel = risk.treatmentStrategy === 'accept';

  // Derive inherent risk level from inherentRiskLevel string
  const inherentRiskLevel = risk.inherentRiskLevel.toLowerCase() as InherentRiskLevel;

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header with back button and actions */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => void navigate({ to: '/risks' })}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('detail.backToRegister')}
          </Button>

          <div className="flex gap-2">
            {canSelectTreatment && (
              <Button
                onClick={() => {
                  setIsTreatmentDialogOpen(true);
                }}
              >
                <ShieldCheck className="mr-2 h-4 w-4" />
                {t('treatment.selectTreatment')}
              </Button>
            )}
          </div>
        </div>

        {/* Risk header (RSK-STR-005) */}
        <RiskDetailHeader
          risk={risk}
          {...(canEdit ? { onEditClick: () => { setIsEditDialogOpen(true); } } : {})}
        />

        {/* AI Leon Assistant */}
        {activeOrg && (
          <AIRiskAssistant
            orgId={activeOrg.id}
            riskId={risk.id}
            riskTitle={risk.title}
            riskDescription={risk.description}
            riskCategory={risk.category}
          />
        )}

        {/* Approval Actions Panel (RSK-STR-022) */}
        {isPendingApproval &&
          approvalStatus?.approvalRequest &&
          currentUser &&
          (approvalStatus.approvalRequest.status === 'pending' ||
            approvalStatus.approvalRequest.status === 'changes_requested') && (
            <ApprovalActionsPanel
              riskId={riskId}
              riskTitle={risk.title}
              approvalRequest={{
                id: approvalStatus.approvalRequest.id,
                status: approvalStatus.approvalRequest.status,
                submittedAt: approvalStatus.approvalRequest.submittedAt,
                submittedBy: approvalStatus.approvalRequest.submittedBy,
                submissionNotes: approvalStatus.approvalRequest.submissionNotes ?? null,
              }}
              currentUserId={currentUser.id}
              canApprove={canApprove ?? false}
              onDecision={handleApprovalDecision}
            />
          )}

        {/* Risk metadata (RSK-STR-005) */}
        <RiskDetailMetadata risk={risk} />

        {/* Risk Assessment Panel (RSK-STR-010) */}
        <RiskAssessmentPanel risk={risk} onAssessmentUpdated={handleEditSuccess} />

        {/* Custom fields (RSK-STR-005) */}
        {risk.customFields && Object.keys(risk.customFields).length > 0 && (
          <RiskDetailCustomFields customFields={risk.customFields} />
        )}

        {/* Treatment Plan Section (RSK-STR-016) */}
        <TreatmentPlanSection
          riskId={riskId}
          riskStatus={risk.status}
          treatmentStrategy={risk.treatmentStrategy ?? null}
        />

        {/* Linked Controls Section (RSK-STR-017) */}
        <RiskLinkedControlsSection riskId={riskId} />

        {/* NIS2 Measure Links */}
        {activeOrg && (
          <NIS2MeasureLinkSection
            orgId={activeOrg.id}
            sourceEntityId={riskId}
            sourceDomain="RSK"
            sourceEntityType="risk"
            sourceEntityTitle={risk.title}
            suggestedMeasures={['a']}
          />
        )}

        {/* Linked Assets Section (RSK-STR-031) */}
        <RiskLinkedAssetsSection riskId={riskId} />

        {/* Linked Vulnerabilities Section (RSK-STR-037) */}
        <RiskLinkedVulnerabilitiesSection riskId={riskId} orgId={activeOrg?.id} />

        {/* Review Schedule Section (RSK-STR-023) */}
        <div className="rounded-lg border bg-card p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-muted-foreground" />
              <div>
                <h3 className="text-sm font-medium">{t('reviewSchedule.title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('detail.configureReviewDesc')}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setIsReviewScheduleOpen(true); }}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('detail.configureSchedule')}
            </Button>
          </div>
        </div>

        {/* Review Schedule Drawer (RSK-STR-023) */}
        <ReviewScheduleDrawer
          riskId={riskId}
          open={isReviewScheduleOpen}
          onOpenChange={setIsReviewScheduleOpen}
        />

        {/* Risk Acceptance Panel (RSK-STR-018) */}
        {showAcceptancePanel && currentUser && (
          <RiskAcceptancePanel
            riskId={riskId}
            currentUserId={currentUser.id}
            canApprove={canApproveAcceptance ?? false}
            isOwner={isAcceptanceOwner}
            onDocumentAcceptance={() => {
              setIsAcceptanceDialogOpen(true);
            }}
            onApprove={() => {
              setIsApproveDialogOpen(true);
            }}
            onReject={() => {
              setIsApproveDialogOpen(true);
            }}
          />
        )}

        {/* Edit Risk Dialog (RSK-STR-004) */}
        <EditRiskDialog
          risk={risk}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSuccess={handleEditSuccess}
        />

        {/* Select Treatment Strategy Dialog */}
        <TreatmentStrategyDialog
          riskId={riskId}
          riskTitle={risk.title}
          inherentRiskLevel={inherentRiskLevel}
          open={isTreatmentDialogOpen}
          onOpenChange={setIsTreatmentDialogOpen}
          onSuccess={handleTreatmentSuccess}
        />

        {/* Risk Acceptance Dialog (RSK-STR-018) */}
        <RiskAcceptanceDialog
          riskId={riskId}
          riskTitle={risk.title}
          inherentRiskScore={risk.inherentRiskScore}
          open={isAcceptanceDialogOpen}
          onOpenChange={setIsAcceptanceDialogOpen}
          onSuccess={handleAcceptanceSuccess}
        />

        {/* Risk Acceptance Approve Dialog (RSK-STR-018) */}
        {acceptanceData?.currentAcceptance && (
          <RiskAcceptanceApproveDialog
            riskId={riskId}
            acceptance={acceptanceData.currentAcceptance}
            open={isApproveDialogOpen}
            onOpenChange={setIsApproveDialogOpen}
            onSuccess={handleAcceptanceApprovalSuccess}
          />
        )}
      </div>
    </div>
  );
}
